/*
 * File: TransparenciesPreventionDialog.js
 * Project: mp-mobile-app
 *
 * Created by vamsi
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Shows an alert dialog when appointment can't be scheduled due to overlapping transparency
 */
import React from "react";
import { observer } from "mobx-react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import DateFormater from "utils/DateFormatter";
import { Typography } from "@material-ui/core";
import PropTypes from "prop-types";

const TransparenciesPreventionDialog = ({ appt, parentCallBack }) => {
  const confirmAction = "confirm"
  const error = Object.assign({}, appt.error);

  const handleAction = async (e, action = "close") => {
    e.persist()
    e.preventDefault()
    if (!appt) return;
    if (action === confirmAction) {
      if (error["confirmAppointmentId"] != undefined) {
        await appt.confirm();
      }
    } else if (error["confirmAppointmentId"] != undefined) {
      await appt.delete();
    }

    const confirmAppointmentId = error.confirmAppointmentId
    delete error["transparencies"];
    delete error["confirmAppointmentId"];
    appt.setError(error);

    if (action === confirmAction) {
      parentCallBack(confirmAppointmentId)
    }
  };

  const dialogTitle = () => {
    if (error.confirmAppointmentId) {
      return "Confirm Appointment!";
    } else {
      return "Appointment and Transparency Overlap!";
    }
  }

  const dialogInformativeText = () => {
    if (error["notScheduleAppointment"] != undefined &&
      error.notScheduleAppointment) {
      return <Typography variant="body1">
        Transparency Rule: Don&#39;t schedule appointments
        <br /><br />
        You can turn off the rule by going to Schedule
        &gt; Transparency Type Reference on the Desktop application
        and click on the rules tab with the transparency selected in the side bar.
        <br />
      </Typography>
    } else {
      return <Typography variant="body1">
        This confirmation is shown because the transparency has
        a &#39;Confirm Appointment Scheduling&#39; rule set.
        <br /><br />
        You can turn off the rule by going to Schedule
        &gt; Transparency Type Reference on the Desktop application
        and click on the rules tab with the transparency selected in the side bar.
        {error.confirmAppointmentId &&
          <React.Fragment>
            <br /><br />
            Do you still want to continue?
          </React.Fragment>
        }
      </Typography>
    }
  }

  const dialogActions = () => {
    return <DialogActions>
      {error.confirmAppointmentId ?
        <React.Fragment>
          <Button onClick={(e) => handleAction(e, confirmAction)} color="primary">
            Yes
          </Button>
          <Button onClick={handleAction}>
            No
          </Button>
        </React.Fragment> :
        <Button onClick={handleAction} color="primary">
          Ok
        </Button>
      }
    </DialogActions>
  }

  const hasTransErrors =
    Array.isArray(error.transparencies) && error.transparencies.length > 0;

  if (hasTransErrors) {
    return <Dialog open>
      <DialogTitle id="scroll-dialog-title" disableTypography>
        <Typography variant="h5">
          {dialogTitle()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error.transparencies && (
          <React.Fragment>
            <Typography variant="h6">Blocking Transparency:</Typography>
            {error.transparencies.map((t, idx) => {
              return (
                <React.Fragment key={idx}>
                  <b>Transparency Name:</b> {t.blockName}
                  <br />
                  <b>Time: </b>
                  {DateFormater.time(t.start) +
                    " - " +
                    DateFormater.time(t.end)}
                  <br />
                </React.Fragment>
              );
            })}
            <br />
            {dialogInformativeText()}
          </React.Fragment>
        )}
      </DialogContent>
      {dialogActions()}
    </Dialog >
  }
}

TransparenciesPreventionDialog.propTypes = {
  appt: PropTypes.object.isRequired,
  parentCallBack: PropTypes.func.isRequired
};

export default observer(TransparenciesPreventionDialog);
